import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import PhotoUpload from "../components/PhotoUpload";
import { toast } from "sonner";
import { Plus, X, Wrench } from "lucide-react";

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing", icon: "🔧" },
  { value: "electrical", label: "Electrical", icon: "⚡" },
  { value: "structural", label: "Structural", icon: "🏗️" },
  { value: "pest_control", label: "Pest control", icon: "🪲" },
  { value: "appliances", label: "Appliances", icon: "🔌" },
  { value: "other", label: "Other", icon: "🛠️" },
];
const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || { label: v, icon: "🛠️" };

const STATUS_STYLES = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  fixed_pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
  reimburse_pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
  disputed: "bg-red-50 text-red-700 border-red-200",
  resolved: "bg-green-50 text-green-700 border-green-200",
};
const STATUS_LABEL = { open: "Open", in_progress: "In progress", fixed_pending: "Pending approval", reimburse_pending: "Reimbursement pending", disputed: "Disputed", resolved: "Closed" };

export default function OFix({ rentalId = null, embedded = false }) {
  const { user } = useAuth();
  const isTenant = user?.role === "tenant";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Rental-scoped when embedded as a phase tab; global on the standalone page.
      const { data } = await api.get(rentalId ? `/rentals/${rentalId}/ofix` : "/ofix");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not load requests."));
    } finally {
      setLoading(false);
    }
  }, [rentalId]);

  useEffect(() => { load(); }, [load]);

  // Rental context (embedded only) — drives who maintains O-Fix + the toggle.
  const [rental, setRental] = useState(null);
  useEffect(() => {
    if (!rentalId) { setRental(null); return; }
    api.get(`/rentals/${rentalId}`).then(({ data }) => setRental(data)).catch(() => {});
  }, [rentalId]);

  const maintainer = rental?.ofix_maintainer === "agent" ? "agent" : "landlord";
  // Landlord always has access; the agent can also act only when they've opted in.
  const isMaintainer = !!rental && (
    user?.id === rental.landlord_id ||
    (maintainer === "agent" && [rental.creator_id, rental.agent_id, rental.broker_admin_id].includes(user?.id))
  );
  const canSetMaintainer = ["agent", "broker_admin"].includes(user?.role);

  const setOfixMaintainer = async (val) => {
    try {
      await api.post(`/rentals/${rentalId}/ofix-maintainer`, { maintainer: val });
      setRental((r) => ({ ...(r || {}), ofix_maintainer: val }));
      toast.success(val === "agent" ? "You'll maintain this property's O-Fix items." : "The landlord will maintain O-Fix items.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail, "Could not update.")); }
  };

  return (
    <div className="space-y-6">
      {canSetMaintainer && rentalId && (
        <div className="card-base p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-800">O-Fix maintenance</div>
            <div className="text-xs text-gray-500">The landlord can always update O-Fix and stays in the loop. Turn on "Me too" to also handle maintenance yourself.</div>
          </div>
          <div className="flex gap-2">
            {[{ v: "landlord", l: "Landlord" }, { v: "agent", l: "Me too" }].map((o) => (
              <button key={o.v} onClick={() => setOfixMaintainer(o.v)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition ${maintainer === o.v ? "border-[#F97316] bg-[#FFF4ED] text-[#F97316]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className={`font-bold font-display flex items-center gap-2 ${embedded ? "text-xl" : "text-3xl"}`}>
            <Wrench className={`text-[#F97316] ${embedded ? "h-5 w-5" : "h-7 w-7"}`} /> O-Fix
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isTenant
              ? "Report maintenance issues for your rented property. Add photos so they get fixed faster."
              : "Maintenance requests raised by your tenants. View, respond and take action."}
          </p>
        </div>
        {isTenant && (
          <button
            onClick={() => setShowCreate(true)}
            className="shrink-0 inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold px-4 py-2.5 rounded-lg transition"
          >
            <Plus className="h-4 w-4" /> Add O-Fix request
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-base p-12 text-center text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-base p-12 text-center">
          <p className="text-gray-600 text-lg">No maintenance requests yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            {isTenant ? "Raise one when something needs fixing." : "You'll see requests here when tenants report issues."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setSelected(it)}
              className="card-base p-5 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{catMeta(it.category).icon}</span>
                  <div>
                    <div className="font-semibold">{it.room || catMeta(it.category).label}</div>
                    <div className="text-xs text-gray-500">{it.source === "checkin" ? "From Start of Tenancy" : it.property_address}</div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[it.status] || ""}`}>
                  {STATUS_LABEL[it.status] || it.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-3 line-clamp-2">{it.description}</p>
              <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                <span>{!isTenant ? it.tenant_name : "You"}</span>
                <span>{formatDate(it.created_at)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateModal rentalId={rentalId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {selected && (
        <DetailModal
          ofixId={selected.id}
          isTenant={isTenant}
          isMaintainer={isMaintainer}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ── Create request (tenant only) ─────────────────────────────────────────────
function CreateModal({ onClose, onCreated, rentalId: fixedRentalId = null }) {
  const [rentals, setRentals] = useState([]);
  const [rentalId, setRentalId] = useState(fixedRentalId || "");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (fixedRentalId) return; // property is fixed when raised from inside a rental
    (async () => {
      try {
        const { data } = await api.get("/rentals");
        const active = data.filter((r) => ["active", "expired_continuing"].includes(r.status));
        setRentals(active);
        if (active.length === 1) setRentalId(active[0].id);
      } catch { /* ignore */ }
    })();
  }, [fixedRentalId]);

  const submit = async () => {
    if (!rentalId) return toast.error("Select a property.");
    if (!category) return toast.error("Select an issue category.");
    if (!description.trim()) return toast.error("Describe the issue.");
    setSubmitting(true);
    try {
      await api.post(`/rentals/${rentalId}/ofix`, { category, description: description.trim(), photo_urls: photos });
      toast.success("Request submitted. Your landlord has been notified.");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not submit request."));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold font-display">New O-Fix request</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          {!fixedRentalId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
              {rentals.length === 0 ? (
                <p className="text-sm text-gray-500">No active rentals found. You can raise requests only after moving in.</p>
              ) : (
                <select
                  value={rentalId}
                  onChange={(e) => setRentalId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                >
                  <option value="">Select property…</option>
                  {rentals.map((r) => <option key={r.id} value={r.id}>{r.property_address}</option>)}
                </select>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Issue category</label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className={`flex flex-col items-center gap-1 py-3 rounded-lg border text-xs font-medium transition ${
                    category === c.value ? "border-[#F97316] bg-[#FFF4ED] text-[#F97316]" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <span className="text-xl">{c.icon}</span>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="e.g. Leaking tap in the bathroom sink, water pooling on the floor."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
            <PhotoUpload value={photos} onChange={setPhotos} maxFiles={6} label="Add photo" />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium">Cancel</button>
            <button
              onClick={submit}
              disabled={submitting || (!fixedRentalId && rentals.length === 0)}
              className="flex-1 px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg font-medium disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Detail + thread + actions ────────────────────────────────────────────────
function DetailModal({ ofixId, isTenant, isMaintainer = false, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [comment, setComment] = useState("");
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  // maintainer action form
  const [action, setAction] = useState("");
  const [respNote, setRespNote] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [reimburseAmount, setReimburseAmount] = useState("");
  const [disputeReason, setDisputeReason] = useState("");

  const reload = useCallback(async () => {
    const { data } = await api.get(`/ofix/${ofixId}`);
    setDoc(data);
  }, [ofixId]);

  useEffect(() => { reload(); }, [reload]);

  if (!doc) return null;
  const isResolved = doc.status === "resolved";
  const canRespond = isMaintainer && !isResolved;
  const pendingApproval = doc.status === "fixed_pending" || doc.status === "reimburse_pending";

  const submitComment = async () => {
    if (!comment.trim() && commentPhotos.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/ofix/${ofixId}/comment`, {
        note: comment.trim(),
        photo_urls: isTenant ? commentPhotos : [],
      });
      setComment(""); setCommentPhotos([]);
      await reload(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not add comment."));
    } finally { setBusy(false); }
  };

  const submitRespond = async () => {
    if (!action) return toast.error("Choose an action.");
    if (action === "dispute" && !disputeReason.trim()) return toast.error("Add a reason for the dispute.");
    let reimburse_amount = null;
    if (action === "reimburse") {
      reimburse_amount = Math.round(Number(reimburseAmount || 0) * 100);
      if (reimburse_amount <= 0) return toast.error("Enter the reimbursement amount.");
    }
    setBusy(true);
    try {
      await api.post(`/ofix/${ofixId}/respond`, {
        action,
        response_note: respNote.trim() || null,
        scheduled_date: schedDate || null,
        reimburse_amount,
        dispute_reason: disputeReason.trim() || null,
      });
      toast.success("Update sent. Tenant notified.");
      setAction(""); setReimburseAmount(""); setDisputeReason(""); setSchedDate(""); setRespNote("");
      await reload(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not send update."));
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/ofix/${ofixId}/approve`);
      toast.success("Approved and closed.");
      await reload(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not approve."));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/ofix/${ofixId}/reject`, { note: comment.trim() || null });
      toast.success("Reopened. The maintainer has been notified.");
      setComment("");
      await reload(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not update."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl my-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{catMeta(doc.category).icon}</span>
            <div>
              <h2 className="text-xl font-bold font-display">{doc.room || catMeta(doc.category).label}</h2>
              <p className="text-xs text-gray-500">{doc.property_address} · {doc.source === "checkin" ? "Start of Tenancy" : doc.tenant_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[doc.status] || ""}`}>
              {STATUS_LABEL[doc.status] || doc.status}
            </span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Action summary */}
          {doc.action && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
              {doc.status === "in_progress" && <span>🔧 Being fixed{doc.scheduled_date ? ` — scheduled ${formatDate(doc.scheduled_date)}` : ""}.</span>}
              {doc.status === "fixed_pending" && <span>✅ Marked fixed — awaiting tenant approval.</span>}
              {doc.status === "reimburse_pending" && <span>💸 Reimbursement offered: ₹{((doc.reimburse_amount || 0) / 100).toLocaleString("en-IN")} — awaiting tenant approval.</span>}
              {doc.status === "disputed" && <span>⚖️ Disputed — logged for end-of-tenancy settlement.{doc.dispute_reason ? ` Reason: ${doc.dispute_reason}` : ""}</span>}
            </div>
          )}

          {/* Thread */}
          <div className="space-y-4">
            {(doc.updates || []).map((u, i) => (
              <div key={i} className="flex gap-3">
                <div className="shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 uppercase">
                  {(u.by_role || "?")[0]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">{u.by_role}</span>
                    <span className="text-xs text-gray-400">{formatDate(u.created_at)}</span>
                    {u.type && u.type !== "comment" && (
                      <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{u.type.replace(/_/g, " ")}</span>
                    )}
                  </div>
                  {u.note && <p className="text-sm text-gray-700 mt-0.5">{u.note}</p>}
                  {u.photo_urls?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {u.photo_urls.map((p, j) => {
                        const src = typeof p === "string" ? p : p?.url;
                        return (
                          <a key={j} href={src} target="_blank" rel="noreferrer">
                            <img src={src} alt="" className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Tenant: resolve + comment */}
          {isTenant && !isResolved && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="Add a comment or update…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                />
                <div className="mt-2"><PhotoUpload value={commentPhotos} onChange={setCommentPhotos} maxFiles={4} label="Add photo" /></div>
              </div>
              {pendingApproval ? (
                <div className="flex gap-3">
                  <button onClick={reject} disabled={busy} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50">Not resolved yet</button>
                  <button onClick={approve} disabled={busy} className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50">✓ Approve &amp; close</button>
                </div>
              ) : (
                <button onClick={submitComment} disabled={busy} className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50">Add comment</button>
              )}
            </div>
          )}

          {/* Maintainer: take action */}
          {canRespond && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Take action</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "fix_myself", l: "I'll fix it" },
                  { v: "fixed", l: "Mark fixed" },
                  { v: "reimburse", l: "Reimburse tenant" },
                  { v: "dispute", l: "Dispute" },
                ].map((a) => (
                  <button
                    key={a.v}
                    onClick={() => setAction(a.v)}
                    className={`py-2.5 rounded-lg border text-xs font-medium transition ${
                      action === a.v ? "border-[#F97316] bg-[#FFF4ED] text-[#F97316]" : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {a.l}
                  </button>
                ))}
              </div>

              {action === "fix_myself" && (
                <div className="space-y-2">
                  <input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  <textarea value={respNote} onChange={(e) => setRespNote(e.target.value)} rows={2}
                    placeholder="Note to tenant (optional) — e.g. contractor details"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                </div>
              )}

              {action === "reimburse" && (
                <div className="flex items-stretch border border-gray-300 rounded-lg overflow-hidden">
                  <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">₹</span>
                  <input type="number" min={1} value={reimburseAmount} onChange={(e) => setReimburseAmount(e.target.value)}
                    placeholder="Amount to reimburse the tenant"
                    className="flex-1 px-3 py-2 text-sm focus:outline-none" />
                </div>
              )}

              {action === "dispute" && (
                <div className="space-y-2">
                  <textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} rows={2}
                    placeholder="Why do you believe this isn't your responsibility? Logged for end-of-tenancy settlement."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                  <p className="text-xs text-gray-400">Logged as evidence — ORE-AI weighs it when settling the deposit at end of tenancy.</p>
                </div>
              )}

              {action && (
                <button onClick={submitRespond} disabled={busy}
                  className="w-full px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg font-medium disabled:opacity-50">
                  {busy ? "Sending…" : "Send update"}
                </button>
              )}

              <div className="pt-1">
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Or just leave a comment…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                <button onClick={submitComment} disabled={busy}
                  className="mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50">Add comment</button>
              </div>
            </div>
          )}

          {isResolved && (
            <div className="border-t border-gray-100 pt-4 text-center text-sm text-green-700 bg-green-50 rounded-lg py-3">
              ✓ Closed{doc.outcome ? ` — ${doc.outcome}` : ""}{doc.resolved_at ? ` on ${formatDate(doc.resolved_at)}` : ""}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
